import type { JSX } from "react"
import { Link } from 'react-router'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { Card, CardContent } from "@/components/ui/card"
import { ErrorLayout } from "@/layouts/ErrorLayout"

/**
 * The following errors are passed as error query parameters to the default or overridden error page.
 *
 * [Documentation](https://next-auth.js.org/configuration/pages#error-page) */
export type ErrorType =
  | 'default'
  | 'configuration'
  | 'bad gateway'
  | 'maintenance'
  | 'accessdenied'
  | 'verification'

export interface ErrorProps {
  url?: URL
  error?: ErrorType
}

interface ErrorView {
  status: number
  heading: string
  message?: JSX.Element
  signin?: JSX.Element
}

/** Renders an error page. */
export default function ErrorPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const error = searchParams.get('error')

  const signinPageUrl = `/auth/login`

  const signInLink = (
    <p>
      <Link to={signinPageUrl} className="text-primary hover:underline">
        {t('authError.signIn')}
      </Link>
    </p>
  )

  const errors: Record<ErrorType, ErrorView> = {
    default: {
      status: 200,
      heading: 'Error',
      signin: signInLink,
    },
    configuration: {
      status: 500,
      heading: 'Server error',
      message: (
        <div className="space-y-2">
          <p>{t('authError.configuration.message1')}</p>
          <p>{t('authError.configuration.message2')}</p>
        </div>
      ),
    },
    'bad gateway': {
      status: 502,
      heading: 'Bad Gateway',
      message: (
        <div className="space-y-2">
          <p>{t('authError.badGateway.message1')}</p>
          <p>{t('authError.badGateway.message2')}</p>
          <p>{t('authError.badGateway.message3')}</p>
        </div>
      ),
      signin: signInLink,
    },
    'maintenance': {
      status: 503,
      heading: 'Maintenance',
      message: <p>{t('authError.maintenance.message')}</p>,
    },
    accessdenied: {
      status: 403,
      heading: 'Access Denied',
      message: <p>{t('authError.accessDenied.message')}</p>,
      signin: signInLink,
    },
    verification: {
      status: 403,
      heading: 'Unable to sign in',
      message: (
        <div className="space-y-2">
          <p>{t('authError.verification.message1')}</p>
          <p>{t('authError.verification.message2')}</p>
        </div>
      ),
      signin: signInLink,
    },
  }

  const { heading, message, signin } = error
    ? errors[error.toLowerCase() as ErrorType]
    : errors.default

  return (
    <ErrorLayout>
      <Card className="mx-8 min-w-[2xl] rounded-md border-border-subtle p-8 md:min-w-[2xl]">
        <CardContent className="space-y-4">
          <h1 className="text-display-2 text-brand-secondary">
            {heading}
          </h1>

          {message && message}

          {signin}
        </CardContent>
      </Card>
    </ErrorLayout>
  )
}
